export const DIMENSION_READING_VALIDATION_STATUS = {
	EMPTY: "empty",
	VALID: "valid",
	INVALID_NUMBER: "invalid-number",
	EXCESS_SCALE: "excess-scale",
	OUT_OF_RANGE: "out-of-range",
} as const;

type DimensionReadingValidationStatus =
	(typeof DIMENSION_READING_VALIDATION_STATUS)[keyof typeof DIMENSION_READING_VALIDATION_STATUS];

interface MeasurementValidation {
	status: DimensionReadingValidationStatus;
	value: number | null;
	message: string | null;
}

const DECIMAL_PLACES = 3;
const MEASUREMENT_MAX = 999.999;
const PLAIN_DECIMAL_PATTERN = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/;
const RADIUS_CODES = new Set(["RA", "RB", "RC", "RD"]);
const LINEAR_DIMENSION_CODES = new Set(["1", "2", "3", "4"]);

/** Shared scalar entry rules for inspections and section specifications. */
export const validateMeasurement = (raw: string): MeasurementValidation => {
	if (raw.trim() === "") {
		return { status: "empty", value: null, message: null };
	}
	if (!PLAIN_DECIMAL_PATTERN.test(raw)) {
		return {
			status: "invalid-number",
			value: null,
			message: "Enter a valid number",
		};
	}
	if ((raw.split(".")[1]?.length ?? 0) > DECIMAL_PLACES) {
		return {
			status: "excess-scale",
			value: null,
			message: "Maximum three decimals",
		};
	}
	const value = Number(raw);
	if (!Number.isFinite(value) || value < 0 || value > MEASUREMENT_MAX) {
		return {
			status: "out-of-range",
			value: null,
			message: "Allowed range: 0.000 to 999.999",
		};
	}
	return { status: "valid", value, message: null };
};

export const formatMeasurement = (value: number) =>
	value.toFixed(DECIMAL_PLACES);

/** Stored values load rounded to the entry precision, so legacy values with
 * more decimals (or float noise like 1e-7) stay editable. */
export const formatStoredMeasurement = (value: number | null | undefined) =>
	value === null || value === undefined ? "" : formatMeasurement(value);

export const formatValidMeasurement = (raw: string) => {
	const validation = validateMeasurement(raw);
	// Preserve invalid text, including excess trailing zeros, for validation.
	return validation.message === null && validation.value !== null
		? formatMeasurement(validation.value)
		: raw;
};

const padsOnBlur = (code: string) =>
	RADIUS_CODES.has(code) ||
	LINEAR_DIMENSION_CODES.has(code) ||
	code === "STHGT";

/** Keep a leading decimal point editable while making its zero explicit. */
export const normalizeMeasurementInput = (raw: string, code: string) =>
	padsOnBlur(code) ? raw.replace(/^(-?)\.(\d*)$/, "$10.$2") : raw;

export const formatMeasurementInput = (raw: string, code: string) =>
	padsOnBlur(code) ? formatValidMeasurement(raw) : raw;
